import { buildReducer } from '../../../shared';
import type { SearchState } from '../../state/data/searchState';
import {
    searchResultResetAction,
    searchFilterFolderOptionClickedAction,
    getSearchResultsCompletedAction,
    getRecipientCacheSuggestionsCompletedAction,
    getFederatedSearchResultsCompletedAction,
    searchAllQueriesExecutedAction,
    searchModeExitClickAction,
} from '../../../searchbox';
import {
    deletePersonasCompletedAction,
    contactFoldersLoadCompletedAction,
    newPersonaClickedAction,
    personaClickedAction,
    searchResultTypeLabelClickedAction,
} from '../../../hub';
import { confirmDeleteFolderClickedAction, menuEntryClickedAction } from '../../../appchrome';
import { parsePeopleHubOptions } from '../../utils/peopleHubOptionsParser';
import * as actions from '../../actions/coreActions';
import { mergeSearchResults } from './searchStateReducerUtils';

const MAX_SEARCH_HISTORY_LENGTH = 100;
const MAX_MRU_LENGTH = 100;

const defaultState: SearchState = {
    searchHistory: [],
    searchResults: {},
    recipientCacheSuggestions: {},
    currentFolderId: undefined,
    defaultFolderId: undefined,
    personaMRU: [],
};

const handleFolderChange = (folderId: string, currentQuery: string, state: SearchState) => {
    if (!currentQuery) {
        return { ...state, currentFolderId: folderId };
    }
    return {
        ...state,
        currentFolderId: folderId,
        searchResults: {
            ...state.searchResults,
            [currentQuery]: {
                personaEmailMap: {},
                isError: false,
                isLoading: true,
            },
        },
    };
};

export default buildReducer<SearchState>()
    .handle(menuEntryClickedAction, state => ({
        ...state,
        currentFolderId: state.defaultFolderId,
    }))
    .handle(searchModeExitClickAction, state => ({
        ...state,
        currentFolderId: state.defaultFolderId,
    }))
    .handle(newPersonaClickedAction, state => ({
        ...state,
        currentFolderId: state.defaultFolderId,
    }))
    .handle(personaClickedAction, (state, action) => {
        const peopleMru = state.personaMRU || [];
        const newPeopleMru = [
            action.payload.hubPersonaId,
            ...peopleMru.filter(element => element !== action.payload.hubPersonaId),
        ].slice(0, MAX_MRU_LENGTH);

        return {
            ...state,
            personaMRU: newPeopleMru,
        };
    })
    .handle(searchResultResetAction, (state, action) => {
        let searchHistory = state.searchHistory;
        if (!searchHistory) {
            searchHistory = [];
        }

        const newHistory = [
            action.payload.searchQuery,
            ...searchHistory.filter(e => e !== action.payload.searchQuery),
        ].slice(0, MAX_SEARCH_HISTORY_LENGTH);

        return {
            ...state,
            searchResults: {
                ...state.searchResults,
                [action.payload.searchQuery]: {
                    personaEmailMap: {},
                    isError: false,
                    isLoading: true,
                },
            },
            searchHistory: newHistory,
        };
    })
    .handle(actions.userConfigurationReceivedAction, (state, action) => {
        const peopleHubOptions = parsePeopleHubOptions(action.payload.userConfig);
        return {
            ...state,
            searchHistory: peopleHubOptions?.searchHistory || state.searchHistory,
            personaMRU: peopleHubOptions?.personaMRU || state.personaMRU,
        };
    })
    .handleDataAction(contactFoldersLoadCompletedAction, (state, action) => ({
        ...state,
        currentFolderId: !state.currentFolderId
            ? action.payload.rootContactFolder?.id
            : state.currentFolderId,
        defaultFolderId: action.payload.rootContactFolder?.id,
    }))
    .handle(confirmDeleteFolderClickedAction, (state, action) => {
        if (action.payload.folderId === state.currentFolderId) {
            return {
                ...state,
                currentFolderId: undefined,
            };
        }

        return state;
    })
    .handleDataAction(searchAllQueriesExecutedAction, (state, action) => ({
        ...state,
        searchResults: {
            ...state.searchResults,
            [action.payload.currentQuery]: {
                ...state.searchResults[action.payload.currentQuery],
                isLoading: false,
            },
        },
    }))
    .handle(searchFilterFolderOptionClickedAction, (state, action) =>
        handleFolderChange(action.payload.folderId, action.payload.currentQuery, state)
    )
    .handle(searchResultTypeLabelClickedAction, (state, action) =>
        handleFolderChange(action.payload.folderId, null, state)
    )
    .handleDataAction(deletePersonasCompletedAction, (state, action) => {
        const newSearchResults = {};
        Object.keys(state.searchResults).forEach(query => {
            newSearchResults[query] = { ...state.searchResults[query] };
            newSearchResults[query].personaEmailMap = {
                ...newSearchResults[query].personaEmailMap,
            };

            action.payload.deletedHubPersonaIds.forEach(
                hubPersonaId => delete newSearchResults[query].personaEmailMap[hubPersonaId]
            );
        });

        return {
            ...state,
            searchResults: newSearchResults,
        };
    })
    .handleDataAction(getRecipientCacheSuggestionsCompletedAction, (state, action) => {
        return {
            ...state,
            recipientCacheSuggestions: {
                ...state.recipientCacheSuggestions,
                [action.payload.query]: action.payload.suggestions.map(
                    persona => persona.hubPersonaId
                ),
            },
        };
    })
    .handleDataAction(
        getFederatedSearchResultsCompletedAction,
        (state, action) => ({
            ...state,
            searchResults: {
                ...state.searchResults,
                [action.payload.queryText]: {
                    ...mergeSearchResults(
                        state.searchResults[action.payload.queryText],
                        action.payload.hubPersonas,
                        false
                    ),
                    isLoading: false,
                },
            },
        }),
        (state, action) => ({
            ...state,
            searchResults: {
                ...state.searchResults,
                [action.payload.queryText]: {
                    personaEmailMap: {},
                    isError: true,
                    isLoading: false,
                },
            },
        })
    )
    .handleDataAction(
        getSearchResultsCompletedAction,
        (state, action) =>
            action.payload.searchedFolder === state.currentFolderId
                ? {
                      ...state,
                      searchResults: {
                          ...state.searchResults,
                          [action.payload.searchQuery]: mergeSearchResults(
                              state.searchResults[action.payload.searchQuery],
                              action.payload.searchResult,
                              action.payload.isRecipientCache
                          ),
                      },
                  }
                : state,
        (state, action) => ({
            ...state,
            searchResults: {
                ...state.searchResults,
                [action.payload.searchQuery]: {
                    personaEmailMap: {},
                    isError: true,
                    isLoading: false,
                },
            },
        })
    )
    .done(defaultState);
export type { Action } from '../../../shared';
